import { useState, useEffect } from "react";
import { Search, Star, StarOff } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  useBusinessList,
  useToggleBusinessFeatured,
} from "@/hooks/useBusinesses";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddFeaturedDialog = ({ open, onOpenChange }: Props) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  const { data, isLoading } = useBusinessList({
    status: "APPROVED",
    search: debouncedSearch || undefined,
    limit: 20,
  });

  const toggleFeatured = useToggleBusinessFeatured();
  const rows = data?.data ?? [];

  const onToggle = (id: string, name: string, currentlyFeatured: boolean) => {
    toggleFeatured.mutate(
      { id, isFeatured: !currentlyFeatured },
      {
        onSuccess: () =>
          toast.success(
            currentlyFeatured
              ? `${name} removed from featured`
              : `${name} added to featured`,
          ),
        onError: (e) => toast.error(e.message),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Featured Businesses</DialogTitle>
        </DialogHeader>
        <div className="relative mb-3">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search approved businesses…"
            className="pl-8"
          />
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border rounded-md border border-border">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Loading…
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              No approved businesses found.
            </p>
          ) : (
            rows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between px-3 py-2.5"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">
                    {row.businessName}
                  </p>
                  <p className="text-xs text-muted-foreground">{row.city}</p>
                </div>
                <button
                  onClick={() =>
                    onToggle(row.id, row.businessName, row.isFeatured)
                  }
                  disabled={toggleFeatured.isPending}
                  className={`ml-3 flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-btn border disabled:opacity-50 shrink-0 ${
                    row.isFeatured
                      ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100"
                      : "bg-muted text-muted-foreground border-border hover:bg-muted/80"
                  }`}
                >
                  {row.isFeatured ? (
                    <>
                      <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                      Featured
                    </>
                  ) : (
                    <>
                      <StarOff className="w-3 h-3" />
                      Feature
                    </>
                  )}
                </button>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
